// Auth API Service
// Handles backend user registration, profile, and credits
// All endpoints require a valid Keycloak Bearer token

import { httpClient, ApiError } from './http.client';
import { AUTH_ENDPOINTS } from './api.constants';

export interface UserProfile {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  creditsRemained: number;
  creditsTotal?: number;
  createdAt?: string;
  updatedAt?: string;
}

interface ProfileResponse {
  data: UserProfile;
}

interface CreditsResponse {
  data: {
    creditsRemained: number;
  };
}

interface RegisterPayload {
  firstName: string;
  lastName: string;
  phone?: string;
}

interface UseCreditPayload {
  amount: number;
}

export interface InviteInfo {
  code: string;
  label: string;
  credits: number;
  planName: string;
  remaining: number;
  expiresAt: string;
}

export interface JoinPayload {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  acceptTerms: true;
  /** 6-digit code mailed by sendInviteOtp */
  otp: string;
}

export interface JoinResult {
  internetUserId: number;
  email: string;
  credits: number;
  planId: number;
  planName: string;
}

export class AuthApiService {
  /**
   * Public: what an invite code offers (label, plan, credits, seats left).
   * Throws ApiError 400/404/410 with details.reason when the code cannot be used.
   */
  static async getInviteInfo(code: string): Promise<InviteInfo> {
    const r = await httpClient.get<{ data: InviteInfo }>(`${AUTH_ENDPOINTS.JOIN}/${encodeURIComponent(code)}`);
    return r.data;
  }

  /**
   * Public: mail a one-time code to the address (step 1 of /join).
   * Throws ApiError 400 (details.fields), 410 (code gone), 429 (details.reason otp_rate), 502 (mail_error).
   */
  static async sendInviteOtp(code: string, email: string): Promise<void> {
    await httpClient.post<{ data: unknown }>(
      `${AUTH_ENDPOINTS.JOIN}/${encodeURIComponent(code)}/send-otp`, { email }, { retries: 0, timeout: 30000 },
    );
  }

  /**
   * Public: create a Keycloak + backend account on the invite's plan.
   * Throws ApiError 400 (details.fields), 409 (email exists), 410 (code gone), 429, 502.
   */
  static async joinWithInvite(code: string, payload: JoinPayload): Promise<JoinResult> {
    // Not idempotent: never retry (a retry after a committed create reads as 409).
    const r = await httpClient.post<{ data: JoinResult }>(
      `${AUTH_ENDPOINTS.JOIN}/${encodeURIComponent(code)}`, payload, { retries: 0, timeout: 60000 },
    );
    return r.data;
  }

  /**
   * Register user profile in backend (Step 3).
   * Call once after first Keycloak login.
   * Returns 201 if created, handles 409 (already exists) gracefully.
   */
  static async registerProfile(data: RegisterPayload): Promise<UserProfile | null> {
    try {
      const response = await httpClient.post<ProfileResponse>(
        AUTH_ENDPOINTS.REGISTER,
        data
      );
      return response.data;
    } catch (error) {
      // 409 = user already registered in backend, not an error
      if (error instanceof ApiError && error.status === 409) {
        return null;
      }
      // Rethrow other errors
      throw error;
    }
  }

  /**
   * Get current user profile (Step 4).
   * Call on each session to get user data + credits.
   */
  static async getProfile(): Promise<UserProfile> {
    const response = await httpClient.get<ProfileResponse>(AUTH_ENDPOINTS.ME);
    return response.data;
  }

  /**
   * Check available credits (Step 5).
   * Call before starting an analysis.
   */
  static async getCredits(): Promise<number> {
    const response = await httpClient.get<CreditsResponse>(AUTH_ENDPOINTS.CREDITS);
    return response.data.creditsRemained;
  }

  /**
   * Use a credit after analysis (Step 6).
   * Call after a successful analysis completes.
   */
  static async useCredit(amount: number = 1): Promise<void> {
    await httpClient.post(AUTH_ENDPOINTS.USE_CREDIT, { amount } as UseCreditPayload);
  }
}
